import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import DataTable, { Column } from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { vendorApi } from '../../services/vendor';
import type { SiteListItem } from '../../types';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const statusVariant = (s: string) =>
  s === 'active' ? 'success' : s === 'pending' ? 'warning' : 'error';

const columns: Column<SiteListItem>[] = [
  { key: 'name', header: 'Site Name', sortable: true },
  { key: 'city', header: 'City', sortable: true },
  { key: 'state', header: 'State', sortable: true },
  { key: 'client_org_name', header: 'Client' },
  { key: 'guard_count', header: 'Guards', sortable: true },
  { key: 'score', header: 'Score', sortable: true,
    render: (row) => (
      <span className={`font-semibold ${getScoreColorClass(getScoreColor(row.score))}`}>
        {toGGScore(row.score)}
      </span>
    ),
  },
  { key: 'vendor_assignment_status', header: 'Status',
    render: (row) => <StatusBadge label={row.vendor_assignment_status} variant={statusVariant(row.vendor_assignment_status)} />,
  },
];

export default function VendorSites() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'sites', page],
    queryFn: () => vendorApi.getSites({ page: String(page) }).then((r) => r.data),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Sites" description="Sites assigned to your organization" />
      <DataTable<SiteListItem>
        columns={columns}
        data={data?.results ?? []}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No sites assigned"
        emptyDescription="Sites will appear here once a client assigns your organization to a site."
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/vendor/sites/${row.id}`)}
      />
    </>
  );
}
